import {
  MessagesSquare,
  Contact,
  Send,
  Workflow,
  ChartPie,
  ShoppingBag,
  Users,
  PlugZap,
  Settings,
  Rocket,
  type LucideIcon,
} from 'lucide-react'
import type { Role } from '@/types/api'
import { i18n } from '@/lib/i18n'

/**
 * Vai trò được vào. Bỏ trống = mọi vai trò.
 *
 * Danh sách này bám đúng chỗ BACKEND thực sự trả 403, không phải phỏng đoán:
 *   /api/v1/analytics/*  → chặn member  (CDP & Phân tích)
 *   /api/v1/automation/* → chặn member  (Tự động hóa)
 * Nguyên tắc BRD §5.3.1: "Không có quyền → KHÔNG render", không render rồi disable.
 */
const MANAGERS: Role[] = ['owner', 'admin', 'manager']
/** Quyền `contacts.import` — vai trò gốc chỉ owner/admin có (manager thì không). */
const ADMINS: Role[] = ['owner', 'admin']

/**
 * Một trang trong menu phụ của một mục (cấp 2). Có `children` → trang có menu con cấp 3 (vd Đội ngũ › Nhân viên AI ›
 * Kho tri thức): bấm vào trang cấp 2 đi `to`, các trang con hiện thụt vào bên dưới.
 */
export interface NavLeaf {
  label: string
  to: string
  roles?: Role[]
  /** Đường dẫn khác cũng thuộc trang này (vd màn sửa nhân viên AI `/ai/train/…` thuộc "Danh sách nhân viên AI"). */
  also?: string[]
  /** Menu con cấp 3 (chỉ một tầng — trang con không lồng thêm). */
  children?: NavLeaf[]
}

/**
 * Một mục của menu chính (cột icon). Có `children` → mục là một nhóm: bấm vào
 * mở trang con đầu tiên được phép, và trang hiện menu phụ bên trái.
 */
export interface NavSection {
  key: string
  label: string
  icon: LucideIcon
  to?: string
  roles?: Role[]
  children?: NavLeaf[]
}

/** Mục đã lọc theo vai trò — `to` luôn có (trang đích khi bấm). */
export interface NavItem extends NavSection {
  to: string
  children?: NavLeaf[]
}

/**
 * Menu chính, bố cục theo SaleSmartly: Nhắn tin → Khách hàng → Tiếp thị → Tự
 * động hoá → Báo cáo → … → Kết nối → Đội ngũ → Cài đặt. Nghiệp vụ riêng của eCDP (nhân
 * viên AI) chen vào chỗ tương ứng.
 */
export const MAIN_NAV: NavSection[] = [
  { key: 'inbox', label: 'Nhắn tin', icon: MessagesSquare, to: '/conversations' },
  {
    // Một mục duy nhất: Danh sách / Khách trùng lặp / Trích xuất SĐT / Phân khúc là TAB trong module (mỗi tab một
    // đường dẫn riêng — xem pages/customers/customers-tabs.tsx). Dữ liệu khách (vòng đời, thuộc tính, sự kiện) ở Cài đặt.
    key: 'customers',
    label: 'Khách hàng',
    icon: Contact,
    to: '/customers',
  },
  {
    key: 'marketing',
    label: 'Tiếp thị',
    icon: Send,
    children: [
      { label: 'Chiến dịch ZBS', to: '/zns-campaigns' },
      { label: 'Chiến dịch kết bạn Zalo', to: '/zalo-friend-campaigns' },
      { label: 'Gửi tin nhắn Zalo hàng loạt', to: '/zalo-broadcast-campaigns' },
    ],
  },
  { key: 'automation', label: 'Tự động hoá', icon: Workflow, to: '/automation', roles: MANAGERS },
  {
    key: 'reports',
    label: 'Báo cáo',
    icon: ChartPie,
    children: [
      { label: 'Tổng quan', to: '/dashboard' },
      { label: 'Phân tích', to: '/analytics', roles: MANAGERS },
      { label: 'Báo cáo đã lưu', to: '/analytics/saved-reports', roles: MANAGERS },
    ],
  },
  // Kết nối có menu phụ riêng (danh sách kênh) — xem pages/integrations.
  { key: 'connect', label: 'Kết nối', icon: PlugZap, to: '/integrations' },
  {
    // Đội ngũ: nhân viên (người) + Nhân viên AI (khung ba tầng, docs/KE-HOACH-KHUNG-AI-3-TANG.md §6) có menu con
    // cấp 3. Bấm "Nhân viên AI" → `/ai` tự chọn Bắt đầu (chưa có nhân viên AI nào trả lời) hoặc danh sách nhân viên AI.
    key: 'team',
    label: 'Đội ngũ',
    icon: Users,
    children: [
      { label: 'Nhân viên', to: '/team/members', roles: MANAGERS },
      {
        label: 'Nhân viên AI',
        to: '/ai',
        children: [
          { label: 'Bắt đầu', to: '/ai/bat-dau' },
          { label: 'Danh sách nhân viên AI', to: '/ai/nhan-vien', also: ['/ai/train'] },
          { label: 'Kho tri thức', to: '/ai/knowledge' },
          // Sản phẩm là NGUỒN DỮ LIỆU (đồng bộ Evotech / Google Sheet / nhập tay) cho nhân viên AI tra cứu và gửi khách.
          { label: 'Sản phẩm', to: '/ai/san-pham' },
          { label: 'Thư mục dùng chung', to: '/ai/thu-vien' },
          { label: 'Báo cáo và theo dõi', to: '/ai/theo-doi' },
          { label: 'Nguồn AI và lượt AI', to: '/ai/nguon', roles: ADMINS },
        ],
      },
    ],
  },
  { key: 'settings', label: 'Cài đặt', icon: Settings, to: '/settings' },
]

/**
 * Mục "Bắt đầu" ở ĐẦU menu chính — chỉ hiện cho chủ tổ chức mới tới khi hoàn thành / bỏ qua trang Bắt đầu
 * (quyết định bằng `showOnboardingEntry` trong lib/onboarding, không lọc theo vai trò ở đây). Có vòng tiến độ.
 */
export const ONBOARDING_NAV: NavSection & { to: string } = {
  key: 'onboarding',
  label: 'Bắt đầu',
  icon: Rocket,
  to: '/onboarding',
}

/** Khoá dịch của một trang theo đường dẫn: `/ai/bat-dau` → `ai_bat-dau` (khoá `nav:page.ai_bat-dau`). */
export const pageKey = (to: string) => to.replace(/^\//, '').replace(/[/.]/g, '_')
/**
 * Nhãn theo ngôn ngữ đang dùng — `label` tiếng Việt ở trên là bản nguồn/dự phòng. Gọi lúc vẽ (không lưu sẵn) nên
 * component dùng menu cần đăng ký đổi ngôn ngữ (`useTranslation()`) để vẽ lại.
 */
export const sectionLabel = (s: Pick<NavSection, 'key' | 'label'>) => i18n.t(`nav:section.${s.key}`, { defaultValue: s.label })
const pageLabel = (c: NavLeaf) => i18n.t(`nav:page.${pageKey(c.to)}`, { defaultValue: c.label })

const allowed = (roles: Role[] | undefined, role: Role) => !roles || roles.includes(role)
/** Trang có trong `PAGE_VIEW_PERM` thì cần đúng quyền xem đó; `perms` chưa biết (đang tải / lỗi) → không lọc. */
const permitted = (perms: Set<string> | undefined, to: string) => !perms || !PAGE_VIEW_PERM[to] || perms.has(PAGE_VIEW_PERM[to])

/** Lọc trang con (cấp 2 và cấp 3) theo vai trò + quyền; trang có menu con mà hết con được phép thì bỏ luôn. */
function leavesForRole(leaves: NavLeaf[], role: Role, perms?: Set<string>): NavLeaf[] {
  const out: NavLeaf[] = []
  for (const c of leaves) {
    if (!allowed(c.roles, role)) continue
    if (!c.children) {
      if (permitted(perms, c.to)) out.push({ ...c, label: pageLabel(c) })
      continue
    }
    const children = c.children
      .filter((g) => allowed(g.roles, role) && permitted(perms, g.to))
      .map((g) => ({ ...g, label: pageLabel(g) }))
    // Không vào được trang gốc của nhóm (vd `/ai` cần ai.view) mà còn trang con được phép → bấm nhóm đi trang con đầu.
    if (children.length) out.push({ ...c, label: pageLabel(c), to: permitted(perms, c.to) ? c.to : children[0].to, children })
  }
  return out
}

/**
 * Lọc menu theo vai trò hiện tại, và theo quyền xem (`/me/permissions`, bảng `PAGE_VIEW_PERM`) khi có `perms` —
 * "Không có quyền → không hiện" (BRD §5.3.1): Nhân viên không có `ai.view` thì không thấy các trang Nhân viên AI
 * (trước đây vẫn hiện rồi mở ra báo "Không tải được"). Server vẫn kiểm tra lại — đây chỉ là lớp trải nghiệm.
 */
export function navForRole(role?: Role, perms?: Set<string>): NavItem[] {
  if (!role) return []
  const out: NavItem[] = []
  for (const s of MAIN_NAV) {
    if (!allowed(s.roles, role)) continue
    if (!s.children) {
      if (permitted(perms, s.to!)) out.push({ ...s, label: sectionLabel(s), to: s.to! })
      continue
    }
    const children = leavesForRole(s.children, role, perms)
    if (!children.length) continue
    // `to` riêng của nhóm (vd `/ai` tự chuyển hướng) thắng trang con đầu tiên.
    out.push({ ...s, label: sectionLabel(s), to: s.to ?? children[0].to, children })
  }
  return out
}

const under = (path: string, to: string) => path === to || path.startsWith(`${to}/`)

/** Độ dài đường khớp dài nhất của trang (kể cả `also` và trang con cấp 3); -1 = không khớp. */
function matchLen(c: NavLeaf, path: string): number {
  const own = [c.to, ...(c.also ?? [])].filter((p) => under(path, p)).map((p) => p.length)
  return Math.max(-1, ...own, ...(c.children ?? []).map((g) => matchLen(g, path)))
}

/** Trong một danh sách trang, trang khớp dài nhất — `/analytics/saved-reports` thắng `/analytics`. */
function bestLeaf(leaves: NavLeaf[] | undefined, path: string): NavLeaf | undefined {
  return leaves
    ?.map((c) => ({ c, len: matchLen(c, path) }))
    .filter((x) => x.len >= 0)
    .sort((a, b) => b.len - a.len)[0]?.c
}

/** Trang con cấp 2 đang mở (trang có menu con cấp 3 được tính là mở khi một trang con của nó đang mở). */
export function activeLeaf(item: NavItem, path: string): NavLeaf | undefined {
  return bestLeaf(item.children, path)
}

/** Trang cấp 3 đang mở bên dưới trang cấp 2 `leaf` (vd `/ai/nhan-vien/b1` → "Danh sách nhân viên AI"). */
export function activeSubLeaf(leaf: NavLeaf, path: string): NavLeaf | undefined {
  return bestLeaf(leaf.children, path)
}

/** Số trang bấm được trong menu phụ của mục (đếm cả cấp 2 lẫn cấp 3) — < 2 thì không cần menu phụ / bảng bật. */
export function subnavPageCount(item: NavItem): number {
  return (item.children ?? []).reduce((n, c) => n + 1 + (c.children?.length ?? 0), 0)
}

export function isSectionActive(item: NavItem, path: string): boolean {
  return item.children ? !!activeLeaf(item, path) : under(path, item.to)
}

/** Mục chính đang mở theo đường dẫn hiện tại. */
export function sectionForPath(nav: NavItem[], path: string): NavItem | undefined {
  return nav.find((s) => isSectionActive(s, path))
}

/**
 * Quyền "xem" (khoá `/me/permissions`) cần cho từng trang — khớp backend
 * `permission-registry.ts`. Trang không có trong bảng = không đòi quyền riêng.
 */
export const PAGE_VIEW_PERM: Record<string, string> = {
  '/conversations': 'conversations.view',
  '/conversations/quick-replies': 'quick_replies.view',
  '/customers': 'contacts.view',
  '/customers/import': 'contacts.import',
  '/customers/duplicates': 'contacts.view',
  '/customers/extract-phones': 'contacts.view',
  '/customers/segments': 'cdp.view',
  '/zns-campaigns': 'zns.view',
  // Chiến dịch Zalo cá nhân có module quyền riêng (trước đây mượn zns.view / automation.update).
  '/zalo-friend-campaigns': 'friend_campaigns.view',
  '/zalo-broadcast-campaigns': 'broadcast_campaigns.view',
  '/automation': 'automation.view',
  '/dashboard': 'dashboard.view',
  '/analytics': 'analytics.view',
  '/analytics/saved-reports': 'analytics.view',
  '/integrations': 'integrations.view',
  '/team/members': 'employees.view',
  '/team/channel-access': 'account_access.view',
  '/ai': 'ai.view',
  '/ai/bat-dau': 'ai.view',
  '/ai/nhan-vien': 'ai.view',
  '/ai/knowledge': 'ai.view',
  '/ai/san-pham': 'products.view',
  '/ai/thu-vien': 'ai.view',
  '/ai/theo-doi': 'ai.view',
  '/ai/nguon': 'ai.view',
  '/settings': 'settings.view',
}

/** Trang cuối cùng khi không có quyền xem trang nào trong menu — ai cũng vào được. */
export const FALLBACK_HOME = '/account/profile'

/**
 * Trang đích sau đăng nhập / khi vào `/` hoặc đường dẫn lạ (D7):
 *  - có quyền chat (`conversations.view`) → Nhắn tin;
 *  - không thì trang ĐẦU TIÊN trong menu (theo thứ tự menu) mà vai trò + quyền cho vào.
 * `perms` chưa biết (lỗi tải quyền) → giữ hành vi cũ là Nhắn tin; server vẫn chặn lại.
 */
export function homePath(role: Role | undefined, perms: Set<string> | undefined): string {
  if (!perms) return '/conversations'
  const ok = (to: string) => !PAGE_VIEW_PERM[to] || perms.has(PAGE_VIEW_PERM[to])
  if (perms.has('conversations.view')) return '/conversations'
  for (const item of navForRole(role)) {
    const pages = item.children?.map((c) => c.to) ?? [item.to]
    const first = pages.find(ok)
    if (first) return first
  }
  return FALLBACK_HOME
}

/**
 * Đường quay lại sau đăng nhập (D9): giữ NGUYÊN pathname + query + hash của trang
 * đã bị chặn (vd `/billing?payment=ok#hoa-don`). Không có / trỏ về chính trang
 * đăng nhập / không phải đường nội bộ → `/` (để chọn trang đầu theo quyền).
 */
/**
 * `?next=/duong-dan` do trang ngoài truyền vào (trang giới thiệu → "Mua gói" → /login?next=/billing/upgrade?plan=pro).
 * Chỉ nhận đường nội bộ (một dấu `/`, chặn `//host`, `/\\host`), bỏ qua chính trang đăng nhập / đăng ký và trang đầu.
 */
export function nextParamTarget(search: string): string | null {
  let v: string | null = null
  try { v = new URLSearchParams(search).get('next') } catch { return null }
  if (!v || !v.startsWith('/') || v.startsWith('//') || v.startsWith('/\\')) return null
  const path = v.split(/[?#]/)[0]
  if (path === '/' || path === '/login' || path === '/register' || path === '/signup') return null
  return v
}

/** Đích sau đăng nhập / đăng ký: `?next=` (từ trang ngoài) trước, rồi trang bị chặn trong app (`location.state.from`). */
export function authRedirectTarget(state: unknown, search = ''): string {
  return nextParamTarget(search) ?? loginRedirectTarget(state)
}

export function loginRedirectTarget(state: unknown): string {
  const from = (state as { from?: { pathname?: unknown; search?: unknown; hash?: unknown } } | null)?.from
  const pathname = typeof from?.pathname === 'string' ? from.pathname : ''
  // Chỉ nhận đường nội bộ: bắt đầu bằng một dấu `/` (chặn `//host` và `/\host`).
  if (!pathname.startsWith('/') || pathname.startsWith('//') || pathname.startsWith('/\\')) return '/'
  if (pathname === '/login' || pathname === '/') return '/'
  const search = typeof from?.search === 'string' ? from.search : ''
  const hash = typeof from?.hash === 'string' ? from.hash : ''
  return `${pathname}${search}${hash}`
}
